<template>
  <el-dialog
    title="管理视频标签"
    :visible.sync="dialogVisible"
    width="95%"
    top="4vh"
    @close="handleClose"
  >
    <div class="marker-manager" v-if="dialogVisible">
      <!-- 左侧：视频播放区域 (75% 宽度) -->
      <div class="manager-left" v-if="videoUrl">
        <div class="preview-video-container">
          <video
            ref="previewVideo"
            :src="videoUrl"
            controls
            preload="metadata"
            playsinline
            style="width: 100%; max-height: 520px; border-radius: 4px;"
            @loadedmetadata="onVideoLoaded"
            @timeupdate="onVideoTimeUpdate"
          ></video>

          <!-- 自定义进度条标签 -->
          <div v-if="markers.length > 0 && videoDuration > 0" class="video-markers">
            <!-- 标签进度轨道 (点击可跳转) -->
            <div class="markers-timeline-track" @click="onMarkersTimelineClick">
              <div class="markers-timeline-played" :style="{ width: (lastVideoTime / videoDuration) * 100 + '%' }"></div>
              <div class="markers-timeline-handle" :style="{ left: (lastVideoTime / videoDuration) * 100 + '%' }"></div>
            </div>

            <div
              v-for="marker in markers"
              :key="marker.id || marker.sort"
              class="video-marker"
              :style="{left: getMarkerPosition(marker.timeSeconds) + '%'}"
              @click="jumpToTime(marker.timeSeconds)"
            >
              <div class="marker-label">{{ marker.label || '标签' }}</div>
              <div class="marker-line"></div>
              <div class="marker-dot"></div>
            </div>
          </div>
        </div>

        <div style="color: #606266; font-size: 14px; margin-top: 15px; font-weight: 500;">
          <i class="el-icon-video-play"></i> 视频总时长：{{ formatDuration(videoDuration) }}
        </div>
      </div>

      <!-- 右侧：管理操作区域 (25% 宽度) -->
      <div class="manager-right">
        <!-- 说明 -->
        <el-alert
          title="为视频增加时间标签，学生可快速跳转并定位章节内容"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 15px;"
        >
        </el-alert>

        <!-- 标签列表 -->
        <div class="markers-list">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-weight: 600; font-size: 14px; color: #303133;">
              <i class="el-icon-collection-tag"></i> 视频标签列表
            </span>
            <el-button type="primary" size="mini" @click="addMarker">
              <i class="el-icon-plus"></i> 添加
            </el-button>
          </div>

          <div class="table-scroll-wrapper">
            <el-table :data="markers" border style="width: 100%" size="mini">
              <el-table-column label="#" width="45" align="center">
                <template slot-scope="scope">
                  {{ scope.$index + 1 }}
                </template>
              </el-table-column>

              <el-table-column label="时间" width="165">
                <template slot-scope="scope">
                  <div style="display: flex; gap: 4px; align-items: center;">
                    <el-time-picker
                      v-model="scope.row.timeValue"
                      format="HH:mm:ss"
                      value-format="HH:mm:ss"
                      placeholder="时间"
                      size="mini"
                      style="width: 100px;"
                      @change="onTimeChange(scope.row)"
                      :picker-options="{
                        selectableRange: getSelectableRange()
                      }"
                    >
                    </el-time-picker>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-video-camera"
                      @click="setCurrentTime(scope.row)"
                      title="使用当前时间"
                      style="padding: 4px;"
                      circle
                      plain
                    ></el-button>
                  </div>
                  <div style="font-size: 10px; color: #909399; margin-top: 2px;">
                    {{ scope.row.timeSeconds }} 秒
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="标签名称" min-width="90">
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.label"
                    placeholder="名称"
                    size="mini"
                    maxlength="10"
                  >
                  </el-input>
                </template>
              </el-table-column>

              <el-table-column label="操作" width="45" align="center">
                <template slot-scope="scope">
                  <el-button
                    type="danger"
                    size="mini"
                    icon="el-icon-delete"
                    @click="deleteMarker(scope.$index)"
                    style="padding: 4px;"
                    circle
                  ></el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div v-if="markers.length === 0" style="text-align: center; padding: 20px; color: #909399; font-size: 12px;">
            暂无标签，点击右上方“添加”开始
          </div>
        </div>

        <!-- 快速生成 -->
        <div class="quick-generate-panel">
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <i class="el-icon-magic-stick" style="font-size: 15px; margin-right: 5px; color: #e6a23c;"></i>
            <span style="font-weight: 600; font-size: 13px; color: #303133;">一键平分生成标签</span>
          </div>
          <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 5px;">
            <el-input-number
              v-model="autoGenerateCount"
              :min="2"
              :max="20"
              size="mini"
              style="width: 80px;"
            ></el-input-number>
            <span style="font-size: 12px; color: #606266;">个标签</span>
            <el-button
              type="warning"
              size="mini"
              icon="el-icon-star-off"
              @click="autoGenerateMarkers"
              style="margin-left: auto;"
            >
              生成
            </el-button>
          </div>
          <div style="font-size: 10px; color: #909399; line-height: 1.3;">
            一键将视频平分为指定段落并自动按序号命名。
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false" size="medium">
        <i class="el-icon-close"></i> 取消
      </el-button>
      <el-button
        type="primary"
        @click="saveMarkers"
        :loading="saving"
        size="medium"
      >
        <i class="el-icon-check"></i> {{ saving ? '保存中...' : '保存标签' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getVideoMarkers, saveVideoMarkers, deleteVideoMarker } from '@/api/amcVideoEnhancement'

export default {
  name: 'VideoMarkerManager',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sectionId: {
      type: Number,
      required: false,
      default: null
    },
    videoUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      markers: [],
      videoDuration: 0,
      lastVideoTime: 0,
      saving: false,
      autoGenerateCount: 17
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadMarkers()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 加载已有标签
    async loadMarkers() {
      try {
        const res = await getVideoMarkers(this.sectionId)
        if (res.code === 1 && res.data) {
          this.markers = res.data.map(m => ({
            ...m,
            timeValue: this.secondsToTimeString(m.timeSeconds)
          }))
        } else {
          this.markers = []
        }
      } catch (e) {
        console.error('加载标签失败', e)
        this.markers = []
      }
    },

    // 将秒数转换为时间字符串 HH:mm:ss
    secondsToTimeString(seconds) {
      const hh = Math.floor(seconds / 3600)
      const mm = Math.floor((seconds % 3600) / 60)
      const ss = seconds % 60
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    },

    // 将时间字符串转换为秒数
    timeStringToSeconds(timeStr) {
      if (!timeStr) return 0
      const parts = timeStr.split(':')
      if (parts.length === 3) {
        const [hh, mm, ss] = parts.map(Number)
        return hh * 3600 + mm * 60 + ss
      }
      return 0
    },

    // 时间选择器变化
    onTimeChange(marker) {
      marker.timeSeconds = this.timeStringToSeconds(marker.timeValue)
    },

    // 获取可选时间范围
    getSelectableRange() {
      if (this.videoDuration > 0) {
        const maxTime = this.secondsToTimeString(Math.floor(this.videoDuration))
        return `00:00:00 - ${maxTime}`
      }
      return '00:00:00 - 23:59:59'
    },

    // 视频加载完成
    onVideoLoaded() {
      if (this.$refs.previewVideo) {
        this.videoDuration = this.$refs.previewVideo.duration
      }
    },

    // 播放时间更新
    onVideoTimeUpdate(e) {
      this.lastVideoTime = e.target.currentTime
    },

    // 获取标签在进度条上的位置百分比
    getMarkerPosition(timeSeconds) {
      if (this.videoDuration === 0) {
        return 0
      }
      return (timeSeconds / this.videoDuration) * 100
    },

    // 跳转到指定时间
    jumpToTime(seconds) {
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.currentTime = seconds
      }
    },

    // 点击自定义标签进度条跳转
    onMarkersTimelineClick(e) {
      if (this.videoDuration > 0 && this.$refs.previewVideo) {
        const rect = e.currentTarget.getBoundingClientRect()
        const clickX = e.clientX - rect.left
        const percentage = Math.max(0, Math.min(1, clickX / rect.width))
        const targetTime = percentage * this.videoDuration
        this.jumpToTime(targetTime)
      }
    },

    // 添加标签
    addMarker() {
      const newMarker = {
        sectionId: this.sectionId,
        timeSeconds: 0,
        timeValue: '00:00:00',
        label: '',
        sort: this.markers.length + 1
      }
      this.markers.push(newMarker)
    },

    // 删除标签
    deleteMarker(index) {
      this.$confirm('确定删除该标签吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.markers.splice(index, 1)
        // 重新排序
        this.markers.forEach((m, i) => {
          m.sort = i + 1
        })
      }).catch(() => {})
    },

    // 设置为当前播放时间
    setCurrentTime(marker) {
      if (this.$refs.previewVideo) {
        const currentTime = Math.floor(this.$refs.previewVideo.currentTime)
        marker.timeSeconds = currentTime
        marker.timeValue = this.secondsToTimeString(currentTime)
      } else {
        this.$message.warning('请先加载视频')
      }
    },

    // 格式化秒数为 mm:ss
    formatSeconds(seconds) {
      if (!seconds && seconds !== 0) return '00:00'
      const mm = Math.floor(seconds / 60)
      const ss = seconds % 60
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    },

    // 格式化时长
    formatDuration(seconds) {
      const hh = Math.floor(seconds / 3600)
      const mm = Math.floor((seconds % 3600) / 60)
      const ss = Math.floor(seconds % 60)
      if (hh > 0) {
        return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
      }
      return `${mm}:${String(ss).padStart(2, '0')}`
    },

    // 自动生成标签
    autoGenerateMarkers() {
      if (this.videoDuration === 0) {
        this.$message.warning('请先加载视频')
        return
      }

      this.$confirm(`将生成${this.autoGenerateCount}个平均分布的标签，是否继续？`, '提示', {
        type: 'warning'
      }).then(() => {
        const count = this.autoGenerateCount
        const interval = Math.floor(this.videoDuration / (count - 1))

        this.markers = []
        for (let i = 0; i < count; i++) {
          let label = ''
          if (i === 0) {
            label = '前言'
          } else if (i === count - 1) {
            label = '结课'
          } else {
            label = String(i)
          }

          const timeSeconds = i === count - 1 ? Math.floor(this.videoDuration) : i * interval

          this.markers.push({
            sectionId: this.sectionId,
            timeSeconds,
            timeValue: this.secondsToTimeString(timeSeconds),
            label,
            sort: i + 1
          })
        }

        this.$message.success('已生成标签，请检查并调整')
      }).catch(() => {})
    },

    // 保存标签
    async saveMarkers() {
      // 验证
      for (let i = 0; i < this.markers.length; i++) {
        const marker = this.markers[i]
        if (!marker.label || marker.label.trim() === '') {
          this.$message.warning(`第${i + 1}个标签名称不能为空`)
          return
        }
        if (marker.timeSeconds === undefined || marker.timeSeconds < 0) {
          this.$message.warning(`第${i + 1}个标签时间点无效`)
          return
        }
      }

      this.saving = true
      try {
        const res = await saveVideoMarkers({
          sectionId: this.sectionId,
          markers: this.markers.map(m => ({
            timeSeconds: m.timeSeconds,
            label: m.label.trim(),
            sort: m.sort
          }))
        })

        if (res.code === 1) {
          this.$message.success('保存成功')
          this.$emit('saved')
          this.dialogVisible = false
        } else {
          this.$message.error(res.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存失败', e)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },

    handleClose() {
      // 依靠 v-if="dialogVisible" 自动销毁视频 DOM，无需手动操作 DOM 属性破坏双向绑定
      this.markers = []
      this.videoDuration = 0
    }
  }
}
</script>

<style scoped>
.marker-manager {
  display: flex;
  gap: 20px;
  height: 620px;
  align-items: stretch;
  padding: 10px 0;
}

/* 左边播放器占 75% */
.manager-left {
  flex: 3;
  display: flex;
  flex-direction: column;
  background: #fdfdfd;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 15px;
}

/* 右边操作区占 25% */
.manager-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
  padding-right: 5px;
}

/* 视频预览容器 */
.preview-video-container {
  position: relative;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  min-height: 360px;
}

.preview-video-container video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 隐藏原生播放器 timeline */
video::-webkit-media-controls-timeline {
  display: none !important;
}
video::-moz-media-controls-timeline {
  display: none !important;
}

/* 视频标签轨道 */
.video-markers {
  position: absolute;
  bottom: 12px;
  left: 15px;
  right: 15px;
  height: 20px;
  pointer-events: auto;
  z-index: 10;
  display: flex;
  align-items: center;
}

.markers-timeline-track {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.markers-timeline-played {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #409EFF;
  border-radius: 2px;
}

.markers-timeline-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: #fff;
  border: 2px solid #409EFF;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}

.markers-timeline-track:hover .markers-timeline-handle {
  transform: translate(-50%, -50%) scale(1.3);
}

.video-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  z-index: 2;
}

.video-marker:hover {
  transform: translate(-50%, -50%) translateY(-2px);
  z-index: 20;
}

.video-marker:hover .marker-label {
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-weight: 600;
  padding: 4px 8px;
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -8px);
}

.video-marker:hover .marker-line {
  background: #409EFF;
  height: 8px;
}

.video-marker:hover .marker-dot {
  transform: scale(1.3);
  background: #409EFF;
}

.marker-label {
  font-size: 11px;
  color: #fff;
  background: rgba(0, 0, 0, 0.85);
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  transition: all 0.2s;
  font-weight: 500;
  line-height: 1.2;
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translate(-50%, 5px);
  opacity: 0;
  visibility: hidden;
}

.marker-line {
  width: 1.5px;
  height: 0;
  background: #409EFF;
  transition: all 0.2s;
  border-radius: 1px;
}

.marker-dot {
  width: 10px;
  height: 10px;
  background: #fff;
  border: 2.5px solid #409EFF;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: all 0.2s;
}

/* 操作区域列表与快速生成面板 */
.markers-list {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 15px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-height: 400px;
}

.table-scroll-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.quick-generate-panel {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 15px;
  background: #fafafa;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  background: #f5f7fa;
  margin: -20px;
  margin-top: 20px;
  border-radius: 0 0 4px 4px;
}

/* 表格样式优化 */
::v-deep .el-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

::v-deep .el-table td {
  padding: 6px 0;
}

::v-deep .el-table--border td,
::v-deep .el-table--border th {
  border-color: #ebeef5;
}

/* 隐藏外层滚动条 */
.manager-right::-webkit-scrollbar {
  width: 6px;
}
.manager-right::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
</style>
